import React, { memo } from "react";

import { DOCUMENT_LABELS, DOCUMENT_SAMPLE_AMOUNTS } from "../consts";
import { StatusDetailCardProps } from "../types";
import StatusNoticeIcon from "./StatusNoticeIcon";

/** Primjer stavki predloška — prodaja vozila ili najam. */
const DRAFT_ITEMS = {
  sale: ["Kia Sorento 1.6 T-GDI", "Zimske gume", "Registracija"],
  rent: ["Najam Kia Sorento · 7 dana", "Dodatni vozač", "Zimske gume"],
};

/** Crte umjesto teksta — dio dokumenta koji nije bitan za prikaz. */
const TextLines: React.FC = () => (
  <div className="space-y-1.5">
    <div className="h-1.5 w-full rounded-full bg-slate-100" />
    <div className="h-1.5 w-5/6 rounded-full bg-slate-100" />
    <div className="h-1.5 w-4/6 rounded-full bg-slate-100" />
  </div>
);

/** Desna kartica scene: što trenutni status znači za dokument. */
const StatusDetailCard: React.FC<StatusDetailCardProps> = ({ documentType, isRent, variant }) => {
  const labels = DOCUMENT_LABELS[documentType];
  const amount = isRent ? DOCUMENT_SAMPLE_AMOUNTS.rent : DOCUMENT_SAMPLE_AMOUNTS.sale;
  const fileName = `${labels.number.toLowerCase()}.pdf`;
  const documentLabel = isRent ? labels.rent : labels.sale;

  switch (variant) {
    case "items":
      return (
        <div className="p-5">
          <div className="text-xs font-bold text-slate-800">Stavke</div>
          <div className="mt-3 space-y-2">
            {(isRent ? DRAFT_ITEMS.rent : DRAFT_ITEMS.sale).map((item) => (
              <div key={item} className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <span className="text-[0.7rem] text-slate-700 truncate">{item}</span>
              </div>
            ))}
            <div className="flex items-center justify-center rounded-md border-2 border-dashed border-blue-200 py-1.5 text-[0.7rem] font-semibold text-blue-600">
              + Dodaj stavku
            </div>
          </div>
        </div>
      );

    case "pdf":
      return (
        <div className="p-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-12 rounded-md bg-rose-50 text-[0.6rem] font-bold text-rose-500 flex-shrink-0">
              PDF
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-slate-800 truncate">{fileName}</div>
              <div className="text-[0.65rem] text-slate-400">Spreman za pregled</div>
            </div>
          </div>
          <div className="mt-4">
            <TextLines />
          </div>
        </div>
      );

    case "email":
      return (
        <div className="p-5">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex-shrink-0">
              <StatusNoticeIcon name="mail" className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-slate-800 truncate">marko.horvat@orytron.com</div>
              <div className="text-[0.65rem] text-slate-400 truncate">{documentLabel} {labels.number}</div>
            </div>
          </div>
          <div className="mt-3">
            <TextLines />
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[0.65rem] font-semibold text-emerald-600">
            <StatusNoticeIcon name="check" className="w-3.5 h-3.5" />
            Isporučeno
          </div>
        </div>
      );

    case "approved":
      return (
        <div className="p-5 text-center">
          <span className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-amber-400 text-white">
            <StatusNoticeIcon name="check" className="w-6 h-6" />
          </span>
          <div className="mt-3 text-sm font-bold text-slate-800">Kupac je prihvatio</div>
          <div className="text-[0.65rem] text-slate-400">Marko Horvat · danas u 14:05</div>
        </div>
      );

    case "signature":
      return (
        <div className="p-5">
          <div className="text-xs font-bold text-slate-800">Potpisi</div>
          {(isRent ? ["Najmodavac", "Najmoprimac"] : ["Prodavatelj", "Kupac"]).map((party) => (
            <div key={party} className="mt-3">
              <svg className="w-24 h-6" viewBox="0 0 80 24" fill="none" stroke="#1D4ED8" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M2 18c6-10 10-12 12-6s-2 8 2 4 8-12 12-8-4 10 2 8 10-10 14-8 2 8 8 6 12-6 22-6" />
              </svg>
              <div className="flex items-center justify-between border-t border-slate-100 pt-1 text-[0.6rem] text-slate-400">
                <span>{party}</span>
                <span className="font-semibold text-emerald-600">Potpisano</span>
              </div>
            </div>
          ))}
        </div>
      );

    case "invoiceLink":
      return (
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Izdan račun</div>
          <div className="mt-1 text-sm font-bold text-slate-800">{DOCUMENT_LABELS.invoice.number}</div>
          <div className="mt-3 flex items-end justify-between border-t border-slate-100 pt-3">
            <span className="text-[0.65rem] text-slate-400">Iznos</span>
            <span className="text-base font-bold text-slate-800">{amount.value}</span>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[0.65rem] font-semibold text-violet-600">
            <StatusNoticeIcon name="receipt" className="w-3.5 h-3.5" />
            Povezan s ugovorom
          </div>
        </div>
      );

    case "fiscal":
      return (
        <div className="p-5">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex-shrink-0">
              <StatusNoticeIcon name="shield" className="w-4 h-4" />
            </span>
            <span className="text-xs font-bold text-slate-800">Porezna uprava</span>
          </div>
          <div className="mt-3 space-y-2">
            <div className="rounded-md bg-slate-50 px-2 py-1.5">
              <div className="text-[0.6rem] text-slate-400">JIR</div>
              <div className="font-mono text-[0.6rem] text-slate-700 truncate">3f7a91c2-6b0e-4d1a-9c55-1e2b7d8a4f10</div>
            </div>
            <div className="rounded-md bg-slate-50 px-2 py-1.5">
              <div className="text-[0.6rem] text-slate-400">ZKI</div>
              <div className="font-mono text-[0.6rem] text-slate-700 truncate">a81c3e5f9d2b47c6e0f1a2b3c4d5e6f7</div>
            </div>
          </div>
        </div>
      );

    case "payment":
      return (
        <div className="p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-800">Uplata</span>
            <span className="text-[0.65rem] text-slate-400">23. 9. 2026.</span>
          </div>
          <div className="mt-1 text-lg font-bold text-emerald-600">{amount.value}</div>
          <div className="mt-3 h-2 rounded-full bg-emerald-100">
            <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500" />
          </div>
          <div className="mt-1 text-[0.6rem] text-slate-400">Plaćeno u cijelosti</div>
        </div>
      );

    case "cancelled":
      return (
        <div className="p-5">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex-shrink-0">
              <StatusNoticeIcon name="x" className="w-4 h-4" />
            </span>
            <span className="text-xs font-bold text-slate-800">Storno</span>
          </div>
          <div className="mt-3 text-[0.7rem] text-slate-500">Razlog</div>
          <div className="text-[0.7rem] font-semibold text-slate-700">{isRent ? "Kupac je odustao od najma" : "Kupac je odustao od kupnje"}</div>
          <div className="mt-3">
            <TextLines />
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default memo(StatusDetailCard);
